import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/buildhome/repo/packages/paste-website/src/layouts/DefaultLayout.tsx";
import { graphql } from 'gatsby';
import { Anchor } from '@twilio-paste/anchor';
import { Avatar } from '@twilio-paste/avatar';
import { Box } from '@twilio-paste/box';
import { Button } from '@twilio-paste/button';
import { Card } from '@twilio-paste/card';
import { Heading } from '@twilio-paste/heading';
import { Stack } from '@twilio-paste/stack';
import { Table, THead, TBody, TFoot, Tr, Th, Td } from '@twilio-paste/table';
import { Text } from '@twilio-paste/text';
import { Tooltip } from '@twilio-paste/tooltip';
import { Truncate } from '@twilio-paste/truncate';
import { UnorderedList, ListItem } from '@twilio-paste/list';
import { InformationIcon } from '@twilio-paste/icons/esm/InformationIcon';
import Changelog from '@twilio-paste/table/CHANGELOG.md';
import { Callout, CalloutTitle, CalloutText } from '../../../components/callout';
import { DoDont, Do, Dont } from '../../../components/DoDont';
import { SidebarCategoryRoutes } from '../../../constants';
export const pageQuery = graphql`
  {
    allPasteComponent(filter: {name: {eq: "@twilio-paste/table"}}) {
      edges {
        node {
          name
          description
          status
          version
        }
      }
    }
    mdx(fields: {slug: {eq: "/components/table/"}}) {
      fileAbsolutePath
      frontmatter {
        slug
        title
      }
      headings {
        depth
        value
      }
    }
  }
`;
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const ComponentHeader = makeShortcode("ComponentHeader");
const PageAside = makeShortcode("PageAside");
const LivePreview = makeShortcode("LivePreview");
const ChangelogRevealer = makeShortcode("ChangelogRevealer");
const layoutProps = {
  pageQuery,
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <ComponentHeader name={props.pageContext.frontmatter.title} categoryRoute={SidebarCategoryRoutes.COMPONENTS} githubUrl="https://github.com/twilio-labs/paste/tree/main/packages/paste-core/components/table" storybookUrl="/?path=/story/components-table--default-table" abstractUrl="https://share.goabstract.com/ec5567dc-6d41-4f8d-b34b-6a5bdb912323" data={props.data.allPasteComponent.edges} mdxType="ComponentHeader" />
    <hr></hr>
    <contentwrapper>
      <PageAside data={props.data.mdx} mdxType="PageAside" />
      <content>
        <h2>{`Guidelines`}</h2>
        <h3>{`About Table`}</h3>
        <p>{`A Table is a grid of static textual and numeric data of the same content structure presented in rows and columns of cells.
For example, a Table would be a good way to represent a list of multiple support agents and their respective skills
and online/offline status.`}</p>
        <h3>{`Accessibility`}</h3>
        <p>{`Tables are used to organize data with a logical relationship in grids. Accessible tables need HTML markup that indicates
header cells and data cells and defines their relationship. Assistive technologies use this information to provide
context to users.`}</p>
        <p>{`Accessibility requirements:`}</p>
        <ul>
          <li parentName="ul">{`Tables must have a `}<inlineCode parentName="li">{`THead`}</inlineCode>{`, containing a Th for each column.`}</li>
          <li parentName="ul">{`Tables must have a `}<inlineCode parentName="li">{`TBody`}</inlineCode>{` wrapping the table body of rows.`}</li>
        </ul>
        <p>{`Accessibility recommendations:`}</p>
        <ul>
          <li parentName="ul">{`It is a best practice to include row headers, which can be added by including a header (`}<inlineCode parentName="li">{`Th`}</inlineCode>{`) as the first cell in
each row (`}<inlineCode parentName="li">{`Tr`}</inlineCode>{`), with `}<inlineCode parentName="li">{`scope="row"`}</inlineCode>{`. This is not relevant for all tables, but you should follow this practice when
the first cell in a row is the key value for that row, such as a username. You can find an example of this in
the section on `}<a parentName="li" {...{
              "href": "#row-headers"
            }}>{`row headers`}</a>{`.`}</li>
          <li parentName="ul">{`Any component can be placed in a Table, but tables should only contain static textual and numeric data, rather
than actionable components like buttons, whenever possible. This is because, if the table is reasonably long—say
50 rows—with multiple actions per row—say 4 actions—a keyboard-only user would have to press the tab key 200
times to get past that table.`}</li>
        </ul>
        <Callout mdxType="Callout">
  <CalloutTitle mdxType="CalloutTitle">🦮 Accessibility insight: navigating tables with a screen reader</CalloutTitle>
  <CalloutText mdxType="CalloutText">
    Learn more about why semantic table markup is important in{' '}
    <Anchor href="https://youtu.be/tlpQ0d2ADNo" showExternal mdxType="Anchor">
      this demo video by Inclusive Components
    </Anchor>{' '}
    that shows what it's like to explore a data table with a screen reader.
  </CalloutText>
        </Callout>
        <Heading as="h5" variant="heading30" mdxType="Heading">
  So, why migrate to Table if you need to include actionable elements?
        </Heading>
        <p>{`Our strong recommendation is to use Table over sticking with the legacy table, even if you need to include
actionable elements, like buttons or anchors. While Table should only contain static information for
the `}<a parentName="p" {...{
            "href": "#accessibility"
          }}>{`accessibility reasons`}</a>{` we've outlined, Table `}<em parentName="p">{`can`}</em>{` support the inclusion of
actionable components when needed, and it is the best option available to you today.`}</p>
        <p>{`Once we have a Data Grid component, our recommendation will be to use that whenever you need to include
actions within a table (such as linking to object details, selecting rows, or deleting rows). Data Grid
will be built on top of Table and will layer in some aria controls that make it easier to navigate with
a keyboard (much like navigating through an Excel spreadsheet). However, until the Data Grid component
is available, our recommendation is to use the Table component; we will just ask that you plan to switch
to the Data Grid when it's ready. Here's why we recommend this over sticking with the legacy table:`}</p>
        <ol>
          <li parentName="ol">{`Using the Table component from Paste, even if it includes actionable elements, is much more accessible
than the legacy table.`}</li>
          <li parentName="ol">{`The visual design of the Paste Table component will be very similar to the Data Grid, so they will look
cohesive when used together in one product or feature, and there will be minimal customer impact when
upgrading from Table to Data Grid in the future.`}</li>
          <li parentName="ol">{`If you use the Table component from Paste, you will benefit from design and technical upgrades, whereas
the legacy table will not receive any updates.`}</li>
          <li parentName="ol">{`Migration from the Paste Table to Data Grid will be much easier than migrating from the legacy table
to Data Grid, as the underlying architecture of the Paste Table and Data Grid will be very similar.
In fact, Data Grid will be built using the Table component.`}</li>
        </ol>
        <h2>{`Examples`}</h2>
        <h3>{`Layouts`}</h3>
        <h4>{`Default`}</h4>
        <p>{`The Default Table uses an automatic table layout algorithm dictated by the browser. The width of the table cells are adjusted to fit the content.`}</p>
        <LivePreview scope={{
          Table,
          THead,
          TBody,
          Tr,
          Th,
          Td,
          Text
        }} language="jsx" mdxType="LivePreview">
  {`<Table>
  <THead>
    <Tr>
      <Th>Date</Th>
      <Th>SID</Th>
      <Th>From</Th>
    </Tr>
  </THead>
  <TBody>
    <Tr>
      <Td>
        <Text as="p">16:24:28 PDT</Text>
        <Text as="p">2020-09-17</Text>
      </Td>
      <Td>
        <Text as="span" fontFamily="fontFamilyCode">
          SM0yc4mxi6cn4z13bte7qmflc2drc85mlp
        </Text>
      </Td>
      <Td>(602) 609-6747</Td>
    </Tr>
    <Tr>
      <Td>
        <Text as="p">16:24:28 PDT</Text>
        <Text as="p">2020-09-17</Text>
      </Td>
      <Td>
        <Text as="span" fontFamily="fontFamilyCode">
          SMl29llgoihx286uhxfb0yc5n0sg391x5n
        </Text>
      </Td>
      <Td>(602) 609-6747</Td>
    </Tr>
    <Tr>
      <Td>
        <Text as="p">16:24:28 PDT</Text>
        <Text as="p">2020-09-17</Text>
      </Td>
      <Td>
        <Text as="span" fontFamily="fontFamilyCode">
          SMxarke3v30fv17hauqn86a7nhgm3b5d87
        </Text>
      </Td>
      <Td>(602) 609-6747</Td>
    </Tr>
  </TBody>
</Table>`}
        </LivePreview>
        <h4>{`Fixed column width`}</h4>
        <p>{`The Fixed Table variant sets equal column widths for the table.`}</p>
        <LivePreview scope={{
          Table,
          THead,
          TBody,
          TFoot,
          Tr,
          Th,
          Td,
          Text
        }} language="jsx" mdxType="LivePreview">
  {`<Table tableLayout="fixed">
  <THead>
    <Tr>
      <Th>Agents</Th>
      <Th>Skills</Th>
      <Th textAlign="right">Tasks</Th>
    </Tr>
  </THead>
  <TBody>
    <Tr>
      <Th scope="row">Adam Brown</Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td textAlign="right">35</Td>
    </Tr>
    <Tr>
      <Th scope="row">Adriana Lovelock</Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td textAlign="right">28</Td>
    </Tr>
    <Tr>
      <Th scope="row">Amanda Cutlack</Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td textAlign="right">7</Td>
    </Tr>
  </TBody>
  <TFoot>
    <Tr>
      <Td/>
      <Td/>
      <Td textAlign="right">70</Td>
    </Tr>
  </TFoot>
</Table>`}
        </LivePreview>
        <h4>{`Specified column widths`}</h4>
        <p>{`THead cells (`}<inlineCode parentName="p">{`Th`}</inlineCode>{`) can be given a width in order to fill a section of the Table row (`}<inlineCode parentName="p">{`Tr`}</inlineCode>{`). Widths can be set with Paste Size Tokens or a number value, e.g. 120px.`}</p>
        <LivePreview scope={{
          Table,
          THead,
          TBody,
          Tr,
          Th,
          Td
        }} language="jsx" mdxType="LivePreview">
  {`<Table>
  <THead>
    <Tr>
      <Th width="size20">First Name</Th>
      <Th>Last Name</Th>
    </Tr>
  </THead>
  <TBody>
    <Tr>
      <Td>Tamir</Td>
      <Td>Rice</Td>
    </Tr>
    <Tr>
      <Td>Philando</Td>
      <Td>Castile</Td>
    </Tr>
    <Tr>
      <Td>Trayvon</Td>
      <Td>Martin</Td>
    </Tr>
    <Tr>
      <Td>Breonna</Td>
      <Td>Taylor</Td>
    </Tr>
    <Tr>
      <Td>Shantel</Td>
      <Td>Davis</Td>
    </Tr>
    <Tr>
      <Td>Walter</Td>
      <Td>Scott</Td>
    </Tr>
  </TBody>
</Table>`}
        </LivePreview>
        <h3>{`Alignment`}</h3>
        <h4>{`Column alignment`}</h4>
        <p>{`You can adjust the text alignment within Table cells (`}<inlineCode parentName="p">{`Td`}</inlineCode>{` or `}<inlineCode parentName="p">{`Th`}</inlineCode>{`). By default they are left-aligned.`}</p>
        <p>{`In general, text should be left-aligned, numbers should be right-aligned, and the header should be aligned with the data.
Data should rarely ever be centered. Numbers only need to be right-aligned when they indicate size or are meant to be
compared to each other. Numbers like ID numbers, phone numbers, etc. should stay left-aligned.`}</p>
        <LivePreview scope={{
          Table,
          THead,
          TBody,
          Tr,
          Th,
          Td
        }} language="jsx" mdxType="LivePreview">
  {`<Table>
  <THead>
    <Tr>
      <Th>First Name</Th>
      <Th textAlign="right">Last Name</Th>
    </Tr>
  </THead>
  <TBody>
    <Tr>
      <Td>Tamir</Td>
      <Td textAlign="right">Rice</Td>
    </Tr>
    <Tr>
      <Td>Philando</Td>
      <Td textAlign="right">Castile</Td>
    </Tr>
    <Tr>
      <Td>Trayvon</Td>
      <Td textAlign="right">Martin</Td>
    </Tr>
    <Tr>
      <Td>Breonna</Td>
      <Td textAlign="right">Taylor</Td>
    </Tr>
    <Tr>
      <Td>Shantel</Td>
      <Td textAlign="right">Davis</Td>
    </Tr>
    <Tr>
      <Td>Walter</Td>
      <Td textAlign="right">Scott</Td>
    </Tr>
  </TBody>
</Table>`}
        </LivePreview>
        <h4>{`Row alignment`}</h4>
        <p>{`Table rows (`}<inlineCode parentName="p">{`Tr`}</inlineCode>{`) can be set with either top, middle, or bottom vertical alignment. By default they are middle-aligned.`}</p>
        <LivePreview scope={{
          Table,
          THead,
          TBody,
          Tr,
          Th,
          Td
        }} language="jsx" mdxType="LivePreview">
  {`<Table>
  <THead>
    <Tr>
      <Th width="size20">Author</Th>
      <Th>Quote</Th>
    </Tr>
  </THead>
  <TBody>
    <Tr verticalAlign="top">
      <Th scope="row">Chinua Achebe</Th>
      <Td>The impatient idealist says: ‘Give me a place to stand and I shall move the earth.' But such a place does not exist. We all have to stand on the earth itself and go with her at her pace.</Td>
    </Tr>
    <Tr verticalAlign="middle">
      <Th scope="row">James Baldwin</Th>
      <Td>Anyone who has ever struggled with poverty knows how extremely expensive it is to be poor.</Td>
    </Tr>
    <Tr verticalAlign="middle">
      <Th scope="row">Dorothy West</Th>
      <Td>To know how much there is to know is the beginning of learning to live.</Td>
    </Tr>
    <Tr verticalAlign="bottom">
      <Th scope="row">W.E.B. Du Bois</Th>
      <Td>Herein lies the tragedy of the age: not that men are poor—all men know something of poverty; not that men are wicked—who is good? Not that men are ignorant—what is truth? Nay, but that men know so little of men.</Td>
    </Tr>
  </TBody>
</Table>`}
        </LivePreview>
        <h3>{`Borderless`}</h3>
        <p>{`Tables can support a `}<inlineCode parentName="p">{`borderless`}</inlineCode>{` variant, which can be useful in cases where the Table is placed inside of another
containing element, such as a `}<a parentName="p" {...{
            "href": "/components/modal/"
          }}>{`modal`}</a>{` or a `}<a parentName="p" {...{
            "href": "/components/card/"
          }}>{`card`}</a>{`. Tables have borders by default, but these can optionally be
turned off by setting `}<inlineCode parentName="p">{`variant="borderless"`}</inlineCode>{` on `}<inlineCode parentName="p">{`Table`}</inlineCode>{`.`}</p>
        <LivePreview scope={{
          Table,
          THead,
          TBody,
          TFoot,
          Tr,
          Th,
          Td,
          Card,
          Text
        }} language="jsx" mdxType="LivePreview">
  {`<Card>
  <Table variant="borderless">
    <THead>
      <Tr>
        <Th>Agents</Th>
        <Th>Skills</Th>
        <Th textAlign="right">Tasks</Th>
      </Tr>
    </THead>
    <TBody>
      <Tr>
        <Th scope="row">Adam Brown</Th>
        <Td>English, French, Sales, Spanish</Td>
        <Td textAlign="right">35</Td>
      </Tr>
      <Tr>
        <Th scope="row">Adriana Lovelock</Th>
        <Td>English, French, Sales, Spanish</Td>
        <Td textAlign="right">28</Td>
      </Tr>
      <Tr>
        <Th scope="row">Amanda Cutlack</Th>
        <Td>English, French, Sales, Spanish</Td>
        <Td textAlign="right">7</Td>
      </Tr>
    </TBody>
    <TFoot>
      <Tr>
        <Td/>
        <Td/>
        <Td textAlign="right">70</Td>
      </Tr>
    </TFoot>
  </Table>
</Card>`}
        </LivePreview>
        <h3>{`Row headers`}</h3>
        <p>{`Tables support row headers in addition to the more commonly seen column headers. It is a `}<a parentName="p" {...{
            "href": "#accessibility"
          }}>{`best practice`}</a>{` to provide
row headers in cases where the first cell in a row is the key value for that row, such as a username or the
name of an email campaign.`}</p>
        <p>{`Row headers can be added by including a `}<inlineCode parentName="p">{`Th`}</inlineCode>{` as the first cell in each `}<inlineCode parentName="p">{`Tr`}</inlineCode>{`, with `}<inlineCode parentName="p">{`scope="row"`}</inlineCode>{`.`}</p>
        <LivePreview scope={{
          Table,
          THead,
          TBody,
          Tr,
          Th,
          Td
        }} language="jsx" mdxType="LivePreview">
  {`<Table>
  <THead>
    <Tr>
      <Th>User</Th>
      <Th>Email Address</Th>
      <Th>Last Login Time</Th>
    </Tr>
  </THead>
  <TBody>
    <Tr>
      <Th scope="row">tamir.rice</Th>
      <Td>tamir@rice.com</Td>
      <Td>12:24:00 PDT</Td>
    </Tr>
    <Tr>
      <Th scope="row">philando.castile</Th>
      <Td>philando@castile.com</Td>
      <Td>08:24:20 PDT</Td>
    </Tr>
    <Tr>
      <Th scope="row">trayvon.martin</Th>
      <Td>trayvon@martin.com</Td>
      <Td>16:05:10 PDT</Td>
    </Tr>
    <Tr>
      <Th scope="row">breonna.taylor</Th>
      <Td>breonna@taylor.com</Td>
      <Td>13:10:28 PDT</Td>
    </Tr>
    <Tr>
      <Th scope="row">shantel.davis</Th>
      <Td>shantel@davis.com</Td>
      <Td>01:04:54 PDT</Td>
    </Tr>
    <Tr>
      <Th scope="row">walter.scott</Th>
      <Td>walter@scott.com</Td>
      <Td>08:19:10 PDT</Td>
    </Tr>
  </TBody>
</Table>`}
        </LivePreview>
        <h3>{`Table with footer`}</h3>
        <p>{`Tables support an optional `}<inlineCode parentName="p">{`TFoot`}</inlineCode>{`, which comes after the `}<inlineCode parentName="p">{`TBody`}</inlineCode>{`. The `}<inlineCode parentName="p">{`TFoot`}</inlineCode>{` must contain at least one `}<inlineCode parentName="p">{`Tr`}</inlineCode>{`.
A good use case for a `}<inlineCode parentName="p">{`TFoot`}</inlineCode>{` is when you want to provide a summary or total for the rows of data in your table.`}</p>
        <p>{`The placement of the `}<inlineCode parentName="p">{`TFoot`}</inlineCode>{` is based on classic HTML table design, so it can not be placed anywhere except at
the bottom of a table. In cases where you do want to show a summary or total, and that information is of high
importance to the user, you can place it above the table or at the top of the table in a regular table row,
rather than using the `}<inlineCode parentName="p">{`TFoot`}</inlineCode>{`.`}</p>
        <LivePreview scope={{
          Table,
          THead,
          TBody,
          TFoot,
          Tr,
          Th,
          Td,
          Text
        }} language="jsx" mdxType="LivePreview">
  {`<Table tableLayout="fixed">
  <THead>
    <Tr>
      <Th>Agents</Th>
      <Th>Skills</Th>
      <Th textAlign="right">Tasks</Th>
    </Tr>
  </THead>
  <TBody>
    <Tr>
      <Th scope="row">Adam Brown</Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td textAlign="right">35</Td>
    </Tr>
    <Tr>
      <Th scope="row">Adriana Lovelock</Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td textAlign="right">28</Td>
    </Tr>
    <Tr>
      <Th scope="row">Amanda Cutlack</Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td textAlign="right">7</Td>
    </Tr>
  </TBody>
  <TFoot>
    <Tr>
      <Th scope="row">Total tasks</Th>
      <Td/>
      <Td textAlign="right">70</Td>
    </Tr>
  </TFoot>
</Table>`}
        </LivePreview>
        <h2>{`Composition Notes`}</h2>
        <p>{`Tables must be created using a combination of:`}</p>
        <ul>
          <li parentName="ul"><inlineCode parentName="li">{`Table`}</inlineCode>{`: The table wrapper`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`THead`}</inlineCode>{`: The header of your table`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`TBody`}</inlineCode>{`: The main body of your table`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`TFoot`}</inlineCode>{`: The footer of your table (optional)`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`Tr`}</inlineCode>{`: A row of cells in your table`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`Th`}</inlineCode>{`: A header cell which can be used as a column title and row title`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`Td`}</inlineCode>{`: A cell of table content`}</li>
        </ul>
        <p>{`Tables fill 100% width of the space of their containing element. Use `}<a parentName="p" {...{
            "href": "/layout"
          }}>{`Layout components`}</a>{` to control the size and
placement of Tables within a page.`}</p>
        <h3>{`Ordering columns`}</h3>
        <p>{`In many cases, each table row has some kind of identifier, such as a username, SID, or friendly name. In cases
where the data in each row is associated with an overarching, named object, the name of that object should generally
be the first column in the table. In these cases, the cells in that first column should be `}<a parentName="p" {...{
            "href": "#row-headers"
          }}>{`Row Headers`}</a>{`.`}</p>
        <p>{`For the remaining columns, you will need to conduct usability testing to hone in on what column ordering works
best for your users and their particular needs. Generally, it is a good practice to put columns that are closely
related to one another (such as a unit price, quantity purchased, and total price) near each other in a table.`}</p>
        <p>{`If you do have actions in your Table (see our `}<a parentName="p" {...{
            "href": "#accessibility"
          }}>{`Accessibility Information`}</a>{` on this), they should
generally be placed in the last column.`}</p>
        <h3>{`Text alignment in cells`}</h3>
        <p>{`In general, text should be left-aligned, numbers should be right-aligned, and the header should be aligned with
the data. Data should rarely ever be centered. Numbers only need to be right-aligned when they indicate size and/or
are meant to be compared to each other. Numbers like ID numbers, phone numbers, etc. should stay left-aligned.`}</p>
        <h3>{`Zebra striping`}</h3>
        <p>{`Tables can have zebra stripes (i.e. alternating row highlighting), which can improve readability of particularly
large data sets. Tables have zebra stripes turned off by default, but these can optionally be turned on by
setting `}<inlineCode parentName="p">{`striped`}</inlineCode>{` on `}<inlineCode parentName="p">{`Table`}</inlineCode>{`.`}</p>
        <LivePreview scope={{
          Table,
          THead,
          TBody,
          TFoot,
          Tr,
          Th,
          Td,
          Text
        }} language="jsx" mdxType="LivePreview">
  {`<Table striped>
  <THead>
    <Tr>
      <Th>Agents</Th>
      <Th>Skills</Th>
      <Th textAlign="right">Tasks</Th>
    </Tr>
  </THead>
  <TBody>
    <Tr>
      <Th scope="row">Adam Brown</Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td textAlign="right">35</Td>
    </Tr>
    <Tr>
      <Th scope="row">Adriana Lovelock</Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td textAlign="right">28</Td>
    </Tr>
    <Tr>
      <Th scope="row">Amanda Cutlack</Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td textAlign="right">7</Td>
    </Tr>
  </TBody>
  <TFoot>
    <Tr>
      <Th scope="row">Total tasks</Th>
      <Td/>
      <Td textAlign="right">70</Td>
    </Tr>
  </TFoot>
</Table>`}
        </LivePreview>
        <h3>{`Table with description text`}</h3>
        <p>{`Each individual table cell can support multiple rows of text, which we'll refer to as "description text".
This is useful when you want to provide related details in a table without adding additional columns, such as
placing a user's email address beneath their username in a single cell under a column header titled "user".`}</p>
        <p>{`Description text is recommended in cases where the user would benefit from having additional context about a
particular cell, but that additional context is not the primary data that is being presented in the table.
For example, in a table that lists a user's email campaigns with open rates, click rates, etc. for each,
it may help the user identify a particular campaign by knowing both the campaign name and what time it was
sent, so the first column of this table may include the campaign name with the send time included as
description text. In this example, the send time is not being compared across rows like the open rates and
click rates are; instead, the send time is used to add additional detail that helps the user identify and
understand data in an individual row.`}</p>
        <LivePreview scope={{
          Table,
          THead,
          TBody,
          Tr,
          Th,
          Td,
          Text
        }} language="jsx" mdxType="LivePreview">
  {`<Table>
  <THead>
    <Tr>
      <Th>User</Th>
      <Th>Skills</Th>
      <Th textAlign="right">Tasks</Th>
    </Tr>
  </THead>
  <TBody>
    <Tr>
      <Th scope="row">
        <Text as="p">
          Adam Brown
        </Text>
        <Text as="p" color="colorTextWeak" fontWeight="fontWeightNormal">adam@brown.com</Text>
      </Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td textAlign="right">35</Td>
    </Tr>
    <Tr>
      <Th scope="row">
        <Text as="p" fontWeight="fontWeightBold">
          Adriana Lovelock
        </Text>
        <Text as="p" color="colorTextWeak" fontWeight="fontWeightNormal">adriana@lovelock.com</Text>
      </Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td textAlign="right">28</Td>
    </Tr>
    <Tr>
      <Th scope="row">
        <Text as="p">
          Amanda Cutlack
        </Text>
        <Text as="p" color="colorTextWeak" fontWeight="fontWeightNormal">amanda@cutlack.com</Text>
      </Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td textAlign="right">7</Td>
    </Tr>
  </TBody>
</Table>`}
        </LivePreview>
        <h3>{`Table with avatars`}</h3>
        <p>{`Tables can be composed using Avatar, which can be used to make a table of user data more scannable.
You can put an avatar next to a user's first and last name using `}<inlineCode parentName="p">{`Stack`}</inlineCode>{` inside a single `}<inlineCode parentName="p">{`Td`}</inlineCode>{`.`}</p>
        <LivePreview scope={{
          Table,
          THead,
          TBody,
          Tr,
          Th,
          Td,
          Text,
          Avatar,
          Box,
          Stack
        }} language="jsx" mdxType="LivePreview">
  {`<Table>
  <THead>
    <Tr>
      <Th>User</Th>
      <Th>Skills</Th>
      <Th textAlign="right">Tasks</Th>
    </Tr>
  </THead>
  <TBody>
    <Tr>
      <Th scope="row">
        <Stack orientation="horizontal" spacing="space40">
          <Avatar size="sizeIcon60" name="Adam Brown" src="/images/avatars/avatar1.png" />
          <Box>
            <Text as="p">
              Adam Brown
            </Text>
            <Text as="p" color="colorTextWeak" fontWeight="fontWeightNormal">adam@brown.com</Text>
          </Box>
        </Stack>
      </Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td textAlign="right">35</Td>
    </Tr>
    <Tr>
      <Th scope="row">
        <Stack orientation="horizontal" spacing="space40">
          <Avatar size="sizeIcon60" name="Adriana Lovelock" src="/images/avatars/avatar4.png" />
          <Box>
            <Text as="p">
              Adriana Lovelock
            </Text>
            <Text as="p" color="colorTextWeak" fontWeight="fontWeightNormal">adriana@lovelock.com</Text>
          </Box>
        </Stack>
      </Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td textAlign="right">28</Td>
    </Tr>
    <Tr>
      <Th scope="row">
        <Stack orientation="horizontal" spacing="space40">
          <Avatar size="sizeIcon60" name="Amanda Cutlack" src="/images/avatars/avatar3.png" />
          <Box>
            <Text as="p">
              Amanda Cutlack
            </Text>
            <Text as="p" color="colorTextWeak" fontWeight="fontWeightNormal">amanda@cutlack.com</Text>
          </Box>
        </Stack>
      </Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td textAlign="right">7</Td>
    </Tr>
  </TBody>
</Table>`}
        </LivePreview>
        <h3>{`Table with status indicators`}</h3>
        <p>{`Tables can support custom compositions using `}<inlineCode parentName="p">{`Box`}</inlineCode>{` and `}<inlineCode parentName="p">{`Text`}</inlineCode>{`, such as custom status indicators.
When including statuses in a table, be sure to include supporting text so color isn't the only
indicator of status.`}</p>
        <p>{`Refer to our Status Indicator pattern (coming soon!) for guidance on displaying statuses in your tables.`}</p>
        <LivePreview scope={{
          Table,
          THead,
          TBody,
          Tr,
          Th,
          Td,
          Box,
          Stack,
          Text
        }} language="jsx" mdxType="LivePreview">
  {`<Table>
  <THead>
    <Tr>
      <Th>Result</Th>
      <Th>SID</Th>
      <Th>Email Address</Th>
    </Tr>
  </THead>
  <TBody>
    <Tr>
      <Td>
        <Box display="flex" alignItems="center">
          <Box width="sizeSquare40" height="sizeSquare40" backgroundColor="colorBackgroundSuccess" borderRadius="borderRadiusCircle" />
          <Text as="p" marginLeft="space30">Valid</Text>
        </Box>
        <Text as="p" color="colorTextWeak" marginLeft="space60">2020-09-17, 16:24:28 PDT</Text>
      </Td>
      <Td>
        <Text as="span" fontFamily="fontFamilyCode">
          SM0yc4mxi6cn4z13bte7qmflc2drc85mlp
        </Text>
      </Td>
      <Td>hayden74@hotmail.com</Td>
    </Tr>
    <Tr>
      <Td>
        <Box display="flex" alignItems="center">
          <Box width="sizeSquare40" height="sizeSquare40" backgroundColor="colorBackgroundWarning" borderRadius="borderRadiusCircle" />
          <Text as="p" marginLeft="space30">Risky</Text>
        </Box>
        <Text as="p" color="colorTextWeak" marginLeft="space60">2020-09-17, 16:24:28 PDT</Text>
      </Td>
      <Td>
        <Text as="span" fontFamily="fontFamilyCode">
          SMl29llgoihx286uhxfb0yc5n0sg391x5n
        </Text>
      </Td>
      <Td>beyrek70@hotmail.com</Td>
    </Tr>
    <Tr>
      <Td>
        <Box display="flex" alignItems="center">
          <Box width="sizeSquare40" height="sizeSquare40" backgroundColor="colorBackgroundError" borderRadius="borderRadiusCircle" />
          <Text as="p" marginLeft="space30">Invalid</Text>
        </Box>
        <Text as="p" color="colorTextWeak" marginLeft="space60">2020-09-17, 16:24:28 PDT</Text>
      </Td>
      <Td>
        <Text as="span" fontFamily="fontFamilyCode">
          SMxarke3v30fv17hauqn86a7nhgm3b5d87
        </Text>
      </Td>
      <Td>kevin@hotmail.com</Td>
    </Tr>
  </TBody>
</Table>`}
        </LivePreview>
        <h3>{`Table with tooltips on headers`}</h3>
        <p>{`A `}<inlineCode parentName="p">{`Th`}</inlineCode>{` can be composed to include `}<a parentName="p" {...{
            "href": "/components/button#link-style-button"
          }}>{`icon buttons`}</a>{`, such as an information icon button with additional
details placed in a `}<a parentName="p" {...{
            "href": "/components/tooltip/"
          }}>{`Tooltip`}</a>{` or `}<a parentName="p" {...{
            "href": "/components/popover/"
          }}>{`Popover`}</a>{`.`}</p>
        <LivePreview scope={{
          Table,
          THead,
          TBody,
          Tr,
          Th,
          Td,
          Text,
          Box,
          Button,
          Tooltip,
          InformationIcon
        }} language="jsx" mdxType="LivePreview">
  {`<Table>
  <THead>
    <Tr>
      <Th>Date</Th>
      <Th>
        <Box display="flex" alignItems="center">
          <Text as="span" marginRight="space10">SID</Text>
          <Tooltip text="SID Number">
            <Button variant="reset" size="reset">
              <InformationIcon decorative={false} title="Open Tooltip" display="block" />
            </Button>
          </Tooltip>
        </Box>
      </Th>
      <Th>From</Th>
    </Tr>
  </THead>
  <TBody>
    <Tr>
      <Td>
        <Text as="p">16:24:28 PDT</Text>
        <Text as="p">2020-09-17</Text>
      </Td>
      <Td>
        <Text as="span" fontFamily="fontFamilyCode">
          SM0yc4mxi6cn4z13bte7qmflc2drc85mlp
        </Text>
      </Td>
      <Td>(602) 609-6747</Td>
    </Tr>
    <Tr>
      <Td>
        <Text as="p">16:24:28 PDT</Text>
        <Text as="p">2020-09-17</Text>
      </Td>
      <Td>
        <Text as="span" fontFamily="fontFamilyCode">
          SMl29llgoihx286uhxfb0yc5n0sg391x5n
        </Text>
      </Td>
      <Td>(602) 609-6747</Td>
    </Tr>
    <Tr>
      <Td>
        <Text as="p">16:24:28 PDT</Text>
        <Text as="p">2020-09-17</Text>
      </Td>
      <Td>
        <Text as="span" fontFamily="fontFamilyCode">
          SMxarke3v30fv17hauqn86a7nhgm3b5d87
        </Text>
      </Td>
      <Td>(602) 609-6747</Td>
    </Tr>
  </TBody>
</Table>`}
        </LivePreview>
        <h3>{`Table with truncated cells`}</h3>
        <p>{`In rare cases, you may want to `}<a parentName="p" {...{
            "href": "/components/truncate"
          }}>{`truncate`}</a>{` the text in a given cell. For example, when a table contains
long URLs, and the user does not need to read the full URL.`}</p>
        <LivePreview scope={{
          Table,
          THead,
          TBody,
          TFoot,
          Tr,
          Th,
          Td,
          Anchor,
          Truncate
        }} language="jsx" mdxType="LivePreview">
  {`<Table tableLayout="fixed">
  <THead>
    <Tr>
      <Th>Agents</Th>
      <Th>Skills</Th>
      <Th>Profile URL</Th>
    </Tr>
  </THead>
  <TBody>
    <Tr>
      <Th scope="row">Adam Brown</Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td>
        <Truncate>
          <Anchor href="/getting-started/about-paste">
            https://paste.twilio.design/getting-started/about-paste
          </Anchor>
        </Truncate>
      </Td>
    </Tr>
    <Tr>
      <Th scope="row">Adriana Lovelock</Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td>
        <Truncate>
          <Anchor href="/getting-started/about-paste">
            https://paste.twilio.design/getting-started/about-paste
          </Anchor>
        </Truncate>
      </Td>
    </Tr>
    <Tr>
      <Th scope="row">Amanda Cutlack</Th>
      <Td>English, French, Sales, Spanish</Td>
      <Td>
        <Truncate>
          <Anchor href="/getting-started/about-paste">
            https://paste.twilio.design/getting-started/about-paste
          </Anchor>
        </Truncate>
      </Td>
    </Tr>
  </TBody>
</Table>`}
        </LivePreview>
        <h2>{`When to use Table`}</h2>
        <p>{`Tables are used to represent static, tabular data. For example, a list of users' first names, last names,
and email addresses.`}</p>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Left-align textual table data, such as an email address." preview={false} mdxType="Do" />
  <Dont title="Don't" body="Don't right- or center-align textual table data." preview={false} mdxType="Dont" />
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Right-align numeric table data that is meant to be compared along a single column, such as the open rate on an email campaign." preview={false} mdxType="Do" />
  <Dont title="Don't" body="Don't left- or center-align numeric table data that is meant to be compared along a single column." preview={false} mdxType="Dont" />
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Provide numeric data that is meant to be compared along a single column at the same level of precision, so that the decimal point aligns across all rows and the numbers can be easily compared. For example, show all numbers to two decimal places." preview={false} mdxType="Do" />
  <Dont title="Don't" body="Don't provide numeric data that is meant to be compared along a single column at differing levels of precision. For example, don't show some numbers to one decimal place and others to two." preview={false} mdxType="Dont" />
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Include supporting text when providing a status in a table row." preview={false} mdxType="Do" />
  <Dont title="Don't" body="Don't use color as the only indicator of status in a table row." preview={false} mdxType="Dont" />
        </DoDont>
        <h2>{`Anatomy`}</h2>
        <h5>{`Table`}</h5>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Property`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default token`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Modifiable?`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`border-color`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`color-border-lighter`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`border-width`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Default: border-width-10, Borderless: border-width-0`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`width`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`100%`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
          </tbody>
        </table>
        <h5>{`THead`}</h5>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Property`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default token`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Modifiable?`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`background-color`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`color-background`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`border-bottom-width`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`border-width-20`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`border-bottom-color`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`color-border-lighter`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
          </tbody>
        </table>
        <h5>{`TBody`}</h5>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Property`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default token`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Modifiable?`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`background-color`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`color-background-body`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
          </tbody>
        </table>
        <h5>{`TFoot`}</h5>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Property`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default token`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Modifiable?`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`background-color`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`color-background`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`border-top-width`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`border-width-20`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`border-top-color`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`color-border-lighter`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
          </tbody>
        </table>
        <h5>{`Tr`}</h5>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Property`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default token`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Modifiable?`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`background-color`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Striped even rows: color-background-row-striped`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`border-bottom-width`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`border-width-10`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`border-bottom-color`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`color-border-lighter`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
          </tbody>
        </table>
        <h5>{`Th`}</h5>
        <Box marginBottom="space60" mdxType="Box">
  <Table tableLayout="fixed" mdxType="Table">
    <THead mdxType="THead">
      <Tr mdxType="Tr">
        <Th mdxType="Th">Property</Th>
        <Th mdxType="Th">Default token</Th>
        <Th mdxType="Th">Modifiable?</Th>
      </Tr>
    </THead>
    <TBody mdxType="TBody">
      <Tr mdxType="Tr">
        <Td mdxType="Td">font-size</Td>
        <Td mdxType="Td">font-size-30</Td>
        <Td mdxType="Td">No</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">line-height</Td>
        <Td mdxType="Td">line-height-30</Td>
        <Td mdxType="Td">No</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">font-weight</Td>
        <Td mdxType="Td">font-weight-semibold</Td>
        <Td mdxType="Td">No</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">padding</Td>
        <Td mdxType="Td">
          <UnorderedList marginBottom="space0" mdxType="UnorderedList">
            <ListItem mdxType="ListItem">Top: space-40</ListItem>
            <ListItem mdxType="ListItem">Bottom: space-40</ListItem>
            <ListItem mdxType="ListItem">Left: space-50</ListItem>
            <ListItem mdxType="ListItem">Right: space-50</ListItem>
          </UnorderedList>
        </Td>
        <Td mdxType="Td">No</Td>
      </Tr>
    </TBody>
  </Table>
        </Box>
        <h5>{`Td`}</h5>
        <Box marginBottom="space60" mdxType="Box">
  <Table tableLayout="fixed" mdxType="Table">
    <THead mdxType="THead">
      <Tr mdxType="Tr">
        <Th mdxType="Th">Property</Th>
        <Th mdxType="Th">Default token</Th>
        <Th mdxType="Th">Modifiable?</Th>
      </Tr>
    </THead>
    <TBody mdxType="TBody">
      <Tr mdxType="Tr">
        <Td mdxType="Td">font-size</Td>
        <Td mdxType="Td">font-size-30</Td>
        <Td mdxType="Td">No</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">line-height</Td>
        <Td mdxType="Td">line-height-30</Td>
        <Td mdxType="Td">No</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">font-weight</Td>
        <Td mdxType="Td">font-weight-normal</Td>
        <Td mdxType="Td">No</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">padding</Td>
        <Td mdxType="Td">
          <UnorderedList marginBottom="space0" mdxType="UnorderedList">
            <ListItem mdxType="ListItem">Top: space-40</ListItem>
            <ListItem mdxType="ListItem">Bottom: space-40</ListItem>
            <ListItem mdxType="ListItem">Left: space-50</ListItem>
            <ListItem mdxType="ListItem">Right: space-50</ListItem>
          </UnorderedList>
        </Td>
        <Td mdxType="Td">No</Td>
      </Tr>
    </TBody>
  </Table>
        </Box>
        <hr></hr>
        <h2>{`Usage Guide`}</h2>
        <h3>{`API`}</h3>
        <h4>{`Installation`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`yarn add @twilio-paste/table - or - yarn add @twilio-paste/core
`}</code></pre>
        <h4>{`Usage`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-jsx"
          }}>{`import {Table} from ‘@twilio-paste/table';

const TableExample: React.FC = () => {
  return (
    <Table tableLayout="fixed" variant="default" striped>
      <THead>
        <Tr verticalAlign="middle">
          <Th width="size40" textAlign="left">Column 1</Th>
          <Th>Column 2</Th>
        </Tr>
      </THead>
      <TBody>
        <Tr>
          <Td>Content</Td>
          <Td>Content</Td>
        </Tr>
      </TBody>
      <TFoot>
        <Tr>
          <Td>Content</Td>
          <Td>Content</Td>
        </Tr>
      </TFoot>
    </Table>
  );
};
`}</code></pre>
        <h4>{`Props`}</h4>
        <h5>{`Table`}</h5>
        <p>{`All the valid HTML attributes for `}<inlineCode parentName="p">{`table`}</inlineCode>{` are supported including the following props:`}</p>
        <h6><inlineCode parentName="h6">{`striped?: boolean`}</inlineCode></h6>
        <p>{`Sets a background color for even rows within the table.`}</p>
        <h6><inlineCode parentName="h6">{`tableLayout?: "auto" | "fixed"`}</inlineCode></h6>
        <p>{`Sets the `}<inlineCode parentName="p">{`table-layout`}</inlineCode>{` style of the Table. Defaults to `}<inlineCode parentName="p">{`auto`}</inlineCode>{`.`}</p>
        <h6><inlineCode parentName="h6">{`variant?: "default" | "borderless"`}</inlineCode></h6>
        <p>{`Sets the `}<inlineCode parentName="p">{`border`}</inlineCode>{` style of the Table. Defaults to `}<inlineCode parentName="p">{`default`}</inlineCode>{`.`}</p>
        <h5>{`Tr Props`}</h5>
        <p>{`All the valid HTML attributes for `}<inlineCode parentName="p">{`tr`}</inlineCode>{` are supported including the following props:`}</p>
        <h6><inlineCode parentName="h6">{`verticalAlign?: "top" | "middle" | "bottom"`}</inlineCode></h6>
        <p>{`Sets the vertical alignment of the content within the Table row. Defaults to `}<inlineCode parentName="p">{`middle`}</inlineCode>{`.`}</p>
        <h5>{`Th Props`}</h5>
        <p>{`All the valid HTML attributes for `}<inlineCode parentName="p">{`th`}</inlineCode>{` are supported including the following props:`}</p>
        <h6><inlineCode parentName="h6">{`textAlign?: "left" | "center" | "right"`}</inlineCode></h6>
        <p>{`Sets the text alignment of the content within the Table cell. Defaults to `}<inlineCode parentName="p">{`left`}</inlineCode>{`.`}</p>
        <h6><inlineCode parentName="h6">{`width?: WidthOptions`}</inlineCode></h6>
        <p>{`Sets the width of a Table cell.`}</p>
        <h5>{`Td Props`}</h5>
        <p>{`All the valid HTML attributes for `}<inlineCode parentName="p">{`td`}</inlineCode>{` are supported including the following props:`}</p>
        <h6><inlineCode parentName="h6">{`textAlign?: "left" | "center" | "right"`}</inlineCode></h6>
        <p>{`Sets the text alignment of the content within the Table cell. Defaults to `}<inlineCode parentName="p">{`left`}</inlineCode>{`.`}</p>
        <ChangelogRevealer mdxType="ChangelogRevealer">
  <Changelog mdxType="Changelog" />
        </ChangelogRevealer>
      </content>
    </contentwrapper>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      